import { Component, OnInit } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { LiftDetail } from '../../models/lift-detail.model';
import { ActivatedRoute, Router } from '@angular/router';
import { ModalComponent } from 'src/app/core/components/modal/modal.component';
import { LiftModuleList } from '../../models/lift-module-list.model';
import { HttpResponse } from '@angular/common/http';
import { LookupDetail } from '../../../core/models/lookup-detail.model';
import { LiftService } from '../../services/lift.service';
import { EntityListRefresh } from 'src/app/core/services/entity-list.refresh';
import { UINotificationService } from 'src/app/core/services/ui-notification.service';
import { NotificationType } from 'src/app/core/models/ui-notification.model';
import { LoginService } from 'src/app/user/services/login.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-lift-detail',
  templateUrl: './lift-detail.component.html',
  styleUrls: ['./lift-detail.component.css']
})
export class LiftDetailComponent implements OnInit {
  form = new FormGroup({});
  lift: LiftDetail;
  modules: LiftModuleList[] = [];
  liftEngines: LookupDetail[] = [];
  liftTypes: LookupDetail[] = [];
  liftBoards: LookupDetail[] = [];
  selectedCompany: number;
  menu = 'basic';
  moduleError = '';

  //Permissions
  canEdit: boolean = false;
  canView: boolean = false;
  canViewErrors: boolean = false;
  canControl: boolean = false;
  canViewParameter: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private liftService: LiftService,
    private entityListRefresh: EntityListRefresh,
    private uiNotificationService: UINotificationService,
    private loginService: LoginService,
    public translateService: TranslateService
  ) { }

  ngOnInit() {
    this.lift = this.route.snapshot.data.lift;
    const response: HttpResponse<LiftModuleList[]> = this.route.snapshot.data.modules;
    this.modules = response.body;

    const response2: HttpResponse<LookupDetail[]> = this.route.snapshot.data.liftEngines;
    this.liftEngines = response2.body;

    const response3: HttpResponse<LookupDetail[]> = this.route.snapshot.data.liftTypes;
    this.liftTypes = response3.body;

    const response4: HttpResponse<LookupDetail[]> = this.route.snapshot.data.liftBoards;
    this.liftBoards = response4.body;

    let liftPermission = this.route.snapshot.data.liftPermission;
    if (!liftPermission) {
      liftPermission = [];
    }
    this.canView = liftPermission.indexOf('0') > -1 || this.loginService.isSuperadmin();
    this.canEdit = liftPermission.indexOf('1') > -1 || this.loginService.isSuperadmin();
    this.canViewErrors = liftPermission.indexOf('2') > -1 || this.loginService.isSuperadmin();
    this.canControl = liftPermission.indexOf('3') > -1 || this.loginService.isSuperadmin();
    this.canViewParameter = liftPermission.indexOf('4') > -1 || this.loginService.isSuperadmin();

    this.form.addControl('module', new FormControl(this.lift.liftModule.code));
    this.form.addControl('address', new FormGroup({
      street: new FormControl('', Validators.required),
      town: new FormControl('', Validators.required),
      zipcode: new FormControl('', [Validators.required, Validators.minLength(5)]),
      country: new FormControl('CZ', Validators.required)
    }));
    this.form.patchValue({
      address: {
        street: this.lift.address.street,
        town: this.lift.address.town,
        zipcode: this.lift.address.zipCode,
        country: this.lift.address.country
      }
    });

    this.form.addControl('lift', new FormGroup({
      description: new FormControl(''),
      floorCount: new FormControl('', Validators.required),
      loadCapacity: new FormControl('', Validators.required),
      liftEngine: new FormControl(1, Validators.required),
      liftType: new FormControl(1, Validators.required),
      liftBoard: new FormControl(1, Validators.required)
    }));

    if (this.lift) {
      this.form.patchValue({
        lift: {
          description: this.lift.description,
          floorCount: this.lift.floorCount,
          loadCapacity: this.lift.loadCapacity,
          liftEngine: this.lift.liftEngine.id,
          liftType: this.lift.liftType.id,
          liftBoard: this.lift.liftBoard.id
        }
      });
    }

    this.selectedCompany = +localStorage.getItem('selectedCompany');

    if (!this.canEdit && !this.loginService.isSuperadmin()) {
      this.form.disable();
    }
  }

  search = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      map(term => term.length < 1 ? []
        : this.modules.filter(v => v.code.toLowerCase().indexOf(term.toLowerCase()) > -1).slice(0, 10))
    )

  formatter = (x: { code: string }) => x.code;

  close(modal: ModalComponent) {
    modal.closeModal(null);
  }

  delete(modal: ModalComponent) {
    if (confirm('Chcete opravdu smazat výtah?')) {
      this.liftService.delete(this.lift.id)
        .subscribe(() => {
          this.entityListRefresh.refresh('lift');
          this.uiNotificationService.add({
            type: NotificationType.Info,
            message: "Výtah byl odstraněn"
          });
          modal.closeModal(null);
        },
          err => {
            const errorMessage = err.error.Message;
            this.entityListRefresh.refresh('lift');
            this.uiNotificationService.add({
              type: NotificationType.Error,
              message: errorMessage
            });
            modal.closeModal(null);
          }
        );
    }
  }

  onSubmit(modal: ModalComponent) {
    if (!this.form.get('module').value) {
      this.moduleError = this.translateService.instant('liftDetail.noModuleNotification');
      return;
    }

    let module = this.modules.filter(x => x.code == this.form.get('module').value)[0];
    if (!module) {
      this.moduleError = this.translateService.instant('liftDetail.noModuleNotification');
      return;
    }

    if (this.form.valid && this.canEdit) {
      this.liftService.update({
        id: this.lift.id,
        liftModuleId: module.id,
        companyId: this.selectedCompany,
        liftTypeId: this.form.get('lift.liftType').value,
        liftEngineId: this.form.get('lift.liftEngine').value,
        liftBoardId: this.form.get('lift.liftBoard').value,
        description: this.form.get('lift.description').value,
        floorCount: this.form.get('lift.floorCount').value,
        isActive: true,
        loadCapacity: this.form.get('lift.loadCapacity').value,
        liftAddress: {
          id: this.lift.address.id,
          street: this.form.get('address.street').value,
          town: this.form.get('address.town').value,
          country: this.form.get('address.country').value,
          zipCode: this.form.get('address.zipcode').value
        }
      })
      .subscribe(() => {
        this.entityListRefresh.refresh('lift');
        this.uiNotificationService.add({
          type: NotificationType.Info,
          message: this.translateService.instant('liftDetail.updateNotification')
        });
        modal.closeModal(null);
        },
        err => {
          const errorMessage = err.error.Message;
          if (errorMessage.includes('lift_lift_module_id')) {
            this.moduleError = this.translateService.instant('liftDetail.moduleDuplicationNotification');
          }
        }
      );
    }
  }

}
